import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ExternalLink, FlaskConical, PlayCircle, RefreshCw } from "lucide-react";

import { apiGet, getApiSettings } from "../../Api/Client.js";
import { EmptyState, Modal, Panel } from "../../Shared/ScreenComponents.jsx";
import "../../Styles/VibeTestingScreen.css";

/* Vibe testing (Avalok) — each project's feature readiness, read live from
 * Avalok (Backend/EnterpriseCore/avalok_views.py does the fetch and sign-in).
 *
 * The table is drawn entirely from what Avalok sends: the header row comes
 * from `columns` (visible ones, in `order`), each cell is looked up by the
 * column's `key` and drawn by the column's `type`. No column name is written
 * here, so a column added, renamed, reordered or removed in Avalok shows up on
 * the next fetch without touching this file. A type this page doesn't know
 * yet falls back to the cell's plain `display` text instead of breaking.
 *
 * Fetched when the page opens, when the project changes and on Refresh — no
 * background polling.
 */

const DASH = "—";

// Avalok's tone vocabulary → the design system's badge variants.
const TONE_CLASS = { success: "ds-badge--success", warning: "ds-badge--warning", danger: "ds-badge--danger", info: "ds-badge--info" };

// Sub-feature status values (fixed by the Avalok contract) → the words shown
// on the project summary tiles. An unknown value still shows, under its own name.
const COUNT_LABELS = { pass: "Working", flaky: "Unstable", fail: "Broken", none: "Not Tested" };

// Intranet's own streaming endpoint for a test video. The backend rewrites
// Avalok's video links to this path; anything else is an ordinary web link.
const VIDEO_PATH = /^\/EnterpriseCore\/vibe-testing\/runs\/[A-Za-z0-9_-]+\/video\/$/;

const byOrder = (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0);
const textOf = (value) => (value === null || value === undefined || value === "" ? DASH : String(value));
// The first contract draft called the text "label"; the final one "display".
const displayOf = (obj) => textOf(obj?.display ?? obj?.label);

function formatDateTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function projectFromRoute(route) {
  const query = (route || "").split("?")[1] || "";
  return new URLSearchParams(query).get("project") || "";
}

// ─── Cells ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (!status) return <span className="Vibe-Muted">{DASH}</span>;
  return (
    <span className={`ds-badge ${TONE_CLASS[status.tone] || ""}`}>
      <span className="ds-badge__dot" />
      {displayOf(status)}
    </span>
  );
}

function CellLink({ link, onWatch }) {
  const href = link?.href;
  const label = displayOf(link) === DASH ? "Open" : displayOf(link);
  if (!href) return null;
  if (VIDEO_PATH.test(href)) {
    return (
      <button type="button" className="Vibe-Link" onClick={() => onWatch({ href, title: label })}>
        <PlayCircle size={13} /> {label}
      </button>
    );
  }
  if (!/^https?:\/\//i.test(href)) return null;
  return (
    <a className="Vibe-Link" href={href} target="_blank" rel="noopener noreferrer">
      {label} <ExternalLink size={12} />
    </a>
  );
}

function HistoryStrip({ history }) {
  if (!Array.isArray(history) || history.length === 0) return null;
  const passed = history.filter(Boolean).length;
  return (
    <span className="Vibe-History" role="img" aria-label={`Recent runs, oldest first: ${passed} of ${history.length} passed`}>
      {history.map((ok, index) => <span key={index} className={ok ? "pass" : "fail"} />)}
    </span>
  );
}

function Cell({ column, cell, onWatch }) {
  if (cell === null || cell === undefined) return <span className="Vibe-Muted">{DASH}</span>;
  if (typeof cell !== "object") return <>{textOf(cell)}</>;

  switch (column.type) {
    case "text":
      return (
        <>
          <span className="Vibe-Cell-Main">{displayOf(cell)}</span>
          {cell.subtext && <span className="Vibe-Cell-Sub">{cell.subtext}</span>}
        </>
      );
    case "status":
      return <StatusBadge status={cell} />;
    case "progress": {
      const percent = typeof cell.percent === "number" ? `${Math.round(cell.percent)}%` : DASH;
      return (
        <>
          <span className="Vibe-Cell-Main ds-num">{percent}</span>
          <span className="Vibe-Cell-Sub">{displayOf(cell)}</span>
          <HistoryStrip history={cell.history} />
        </>
      );
    }
    case "details":
      return (
        <>
          <span className="Vibe-Cell-Main">{displayOf(cell)}</span>
          {Array.isArray(cell.fields) && cell.fields.length > 0 && (
            <dl className="Vibe-Fields">
              {cell.fields.map((field, index) => (
                <React.Fragment key={`${field?.label}-${index}`}>
                  <dt>{textOf(field?.label)}</dt>
                  <dd>{textOf(field?.value)}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </>
      );
    case "datetime":
      return (
        <>
          <span className="Vibe-Cell-Main" title={formatDateTime(cell.value)}>{displayOf(cell)}</span>
          {cell.link && <CellLink link={cell.link} onWatch={onWatch} />}
        </>
      );
    case "number":
      return <span className="ds-num">{textOf(cell.display ?? cell.value)}</span>;
    case "link":
      return cell.href ? <CellLink link={cell} onWatch={onWatch} /> : <>{displayOf(cell)}</>;
    default:
      // A column type Avalok added after this page was written.
      return <>{displayOf(cell)}</>;
  }
}

// ─── Feature group ───────────────────────────────────────────────────────────

// Names of the sub-features that are broken or unstable, so a closed group
// still says what is wrong inside it. Found through the column types, not
// column names: the first "status" column and the first "text" column.
function troubleSummary(group, columns) {
  const statusKey = columns.find((c) => c.type === "status")?.key;
  const nameKey = columns.find((c) => c.type === "text")?.key;
  if (!statusKey || !nameKey) return "";
  const names = (group.rows || [])
    .filter((row) => ["fail", "flaky"].includes(row?.cells?.[statusKey]?.value))
    .map((row) => row.cells?.[nameKey]?.display ?? row.cells?.[nameKey]?.label)
    .filter(Boolean);
  if (!names.length) return "";
  const shown = names.slice(0, 2).join(", ");
  return names.length > 2 ? `Failing: ${shown} and ${names.length - 2} more` : `Failing: ${shown}`;
}

function FeatureGroup({ group, columns, onWatch }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => [...(group.rows || [])].sort(byOrder), [group.rows]);
  const summary = group.summary || {};
  const percent = typeof summary.percent === "number" ? Math.min(Math.max(summary.percent, 0), 100) : null;
  const summaryText = summary.display
    || (summary.total !== undefined ? `${summary.working ?? 0} of ${summary.total} working${percent !== null ? ` · ${percent}%` : ""}` : "");
  const trouble = troubleSummary(group, columns);
  const bodyId = `vibe-group-${group.id}`;

  return (
    <section className="ds-card Vibe-Group">
      <button type="button" className="Vibe-Group-Head" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(!open)}>
        <span className="Vibe-Group-Title">
          <span className="ds-h3">{textOf(group.title)}</span>
          {group.description && <span className="Vibe-Cell-Sub">{group.description}</span>}
          {trouble && <span className="Vibe-Group-Trouble">{trouble}</span>}
        </span>
        <span className="Vibe-Group-Meta">
          {summaryText && <span className="Vibe-Group-Count ds-num">{summaryText}</span>}
          {percent !== null && <span className="Vibe-Bar" aria-hidden="true"><span style={{ width: `${percent}%` }} /></span>}
          <StatusBadge status={group.status} />
          <ChevronDown size={16} className={open ? "Vibe-Chevron open" : "Vibe-Chevron"} />
        </span>
      </button>

      {open && (
        <div id={bodyId} className="Vibe-Group-Body">
          {rows.length === 0 ? (
            <EmptyState label="No sub-features in Avalok for this feature yet." />
          ) : (
            <div className="ds-tablewrap">
              <table className="ds-table Vibe-Table">
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key} style={{ textAlign: column.align || "left" }}>{textOf(column.header ?? column.key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id ?? index}>
                      {columns.map((column) => (
                        <td key={column.key} className={`Vibe-Td Vibe-Td--${String(column.type || "text").replace(/[^a-z0-9_-]/gi, "")}`} style={{ textAlign: column.align || "left" }}>
                          <Cell column={column} cell={row.cells?.[column.key]} onWatch={onWatch} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Screen ──────────────────────────────────────────────────────────────────

export function VibeTestingScreen({ route, navigate }) {
  const routeProject = projectFromRoute(route);

  const [projects, setProjects] = useState(null);
  const [projectsError, setProjectsError] = useState("");
  const [table, setTable] = useState(null);
  const [tableError, setTableError] = useState("");
  const [loading, setLoading] = useState(false);
  const [video, setVideo] = useState(null);
  const requestSeq = useRef(0);

  const projectId = routeProject || projects?.[0]?.id || "";

  const loadProjects = useCallback(() => {
    setProjectsError("");
    apiGet("/EnterpriseCore/vibe-testing/projects/")
      .then((res) => setProjects(Array.isArray(res?.projects) ? res.projects : []))
      .catch((error) => { setProjects(null); setProjectsError(error.message || "Couldn't load projects from Avalok."); });
  }, []);

  const loadTable = useCallback((id) => {
    if (!id) return;
    // Only the newest request may write: switching project mid-load must not
    // let the old project's table land on top of the new one.
    const seq = ++requestSeq.current;
    setLoading(true);
    setTableError("");
    apiGet(`/EnterpriseCore/vibe-testing/projects/${encodeURIComponent(id)}/table/`)
      .then((res) => { if (seq === requestSeq.current) setTable(res); })
      .catch((error) => {
        if (seq !== requestSeq.current) return;
        setTable(null);
        setTableError(error.message || "Couldn't load this project's table from Avalok.");
      })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { setTable(null); loadTable(projectId); }, [projectId, loadTable]);

  const refresh = () => {
    if (projectsError || !projects) loadProjects();
    else loadTable(projectId);
  };

  const columns = useMemo(
    () => (table?.columns || []).filter((c) => c && c.key && c.visible !== false).sort(byOrder),
    [table],
  );
  const groups = useMemo(() => [...(table?.groups || [])].sort(byOrder), [table]);
  const project = table?.project;
  const counts = project?.counts && typeof project.counts === "object" ? Object.entries(project.counts) : [];
  const videoSrc = video ? `${(getApiSettings().apiBase || "/api").replace(/\/$/, "")}${video.href}` : "";
  const knownProject = !projects || projects.some((p) => p.id === projectId);

  return (
    <section className="Screen-Stack Vibe-Screen">
      <header className="ds-page-header">
        <div>
          <span className="ds-page-header__eyebrow">Development</span>
          <h1 className="ds-page-header__title">
            <FlaskConical size={22} strokeWidth={1.8} style={{ verticalAlign: "-3px" }} /> Vibe testing (Avalok)
          </h1>
          <p className="ds-page-header__sub">
            What works and what doesn't in each project, pulled straight from Avalok.
          </p>
        </div>
        <div className="ds-page-header__actions">
          {projects && projects.length > 0 && (
            <select
              className="ds-input Vibe-Project-Select"
              aria-label="Project"
              value={projectId}
              onChange={(event) => navigate(`/vibe-testing/?project=${encodeURIComponent(event.target.value)}`)}
            >
              {!knownProject && <option value={projectId}>{projectId}</option>}
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button type="button" className="ds-btn Vibe-Refresh" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} className={loading ? "Vibe-Spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      {projectsError && (
        <Panel title="Can't load Vibe testing projects">
          <p className="Vibe-Message">{projectsError}</p>
        </Panel>
      )}

      {projects && projects.length === 0 && (
        <Panel title="No projects yet">
          <p className="Vibe-Message">Avalok hasn't shared any Vibe testing projects with Intranet.</p>
        </Panel>
      )}

      {tableError && (
        <Panel title="Can't load this project">
          <p className="Vibe-Message">{tableError}</p>
        </Panel>
      )}

      {!table && !tableError && !projectsError && projects?.length !== 0 && (
        <p className="Vibe-Message">Loading from Avalok…</p>
      )}

      {table && (
        <>
          <div className="ds-stat-grid">
            <div className="ds-stat">
              <span className="ds-stat__label">{textOf(project?.name)}</span>
              <span><StatusBadge status={project?.status} /></span>
              {project?.last_change && (
                <span className="Vibe-Cell-Sub" title={formatDateTime(project.last_change.at)}>
                  {displayOf(project.last_change)}
                </span>
              )}
            </div>
            {counts.map(([key, value]) => (
              <div key={key} className="ds-stat">
                <span className="ds-stat__label">{COUNT_LABELS[key] || key}</span>
                <span className="ds-stat__value">{textOf(value)}</span>
              </div>
            ))}
          </div>

          {groups.length === 0 ? (
            <EmptyState label="This project has no features in Avalok yet." />
          ) : (
            <div className="Vibe-Groups">
              {groups.map((group, index) => (
                <FeatureGroup key={group.id ?? index} group={group} columns={columns} onWatch={setVideo} />
              ))}
            </div>
          )}

          {table.generated_at && (
            <p className="Vibe-Footnote">From Avalok, as of {formatDateTime(table.generated_at)}.</p>
          )}
        </>
      )}

      {video && (
        <Modal title={video.title} onClose={() => setVideo(null)} wide>
          {video.failed ? (
            <p className="Vibe-Message">This test video couldn't be loaded from Avalok. It may not exist, or Intranet may not have access to videos yet.</p>
          ) : (
            <video
              className="Vibe-Video"
              src={videoSrc}
              controls
              autoPlay
              preload="metadata"
              onError={() => setVideo((current) => (current ? { ...current, failed: true } : current))}
            >
              Your browser can't play this video.
            </video>
          )}
        </Modal>
      )}
    </section>
  );
}
